import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import PrintShell from "../../components/UI/PrintShell";
import StaffSchedulePrintSheet from "./components/staffSchedule/StaffSchedulePrintSheet";
import {
  getPatientById,
  getStaffSchedule,
} from "../../services/patientService";
import { loadPatientUiConfig } from "./usePatientTabs";

function extractPatient(patientRes) {
  const payload = patientRes?.result || patientRes?.data || patientRes;
  return payload?.patient || payload || null;
}

const PatientStaffSchedulePrintPage = () => {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const chartId = searchParams.get("chartId");
  const [patient, setPatient] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setReady(false);
    setError("");

    (async () => {
      try {
        const uiConfig = await loadPatientUiConfig();
        if (cancelled) return;
        if (!uiConfig.enabledTabKeys.includes("staff")) {
          setError("Staff Assignment is not enabled for this agency");
          return;
        }

        const patientRes = await getPatientById(
          patientId,
          chartId ? { chartId: Number(chartId) } : {},
        );
        if (cancelled) return;
        const nextPatient = extractPatient(patientRes);
        if (!nextPatient?.id && !nextPatient?.mrn) {
          throw new Error("Patient record was empty");
        }
        setPatient(nextPatient);

        const scheduleRes = await getStaffSchedule(
          patientId,
          chartId ? { chartId: Number(chartId) } : {},
        );
        if (cancelled) return;
        setRows(scheduleRes.result?.rows || scheduleRes.rows || []);
        setReady(true);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load staff schedule");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [patientId, chartId]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 px-6">
        <div className="max-w-sm rounded-xl border border-slate-200 bg-white px-6 py-5 text-center shadow-sm">
          <p className="text-sm font-semibold text-slate-800">
            Staff schedule could not be loaded
          </p>
          <p className="mt-1 text-xs text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen animate-pulse bg-slate-100 py-10">
        <div className="mx-auto h-[500px] w-full max-w-4xl rounded-xl border border-slate-200 bg-white" />
      </div>
    );
  }

  return (
    <PrintShell
      title="Staff Schedule"
      ready={ready}
      documentTitle={`Staff Schedule — ${patient.firstName || ""} ${patient.lastName || ""}`.trim()}
    >
      <StaffSchedulePrintSheet patient={patient} rows={rows} />
    </PrintShell>
  );
};

export default PatientStaffSchedulePrintPage;
